import { create } from 'zustand';
import type { UserAccount, UserProperties, ClientAppInfo } from '../types';
import semverCompare from 'semver-compare';
import { setCookie, deleteCookie } from '../lib/cookie-utils';

const TOKEN_STORAGE_KEY = 'folge-session-token';
const ACCOUNT_STORAGE_KEY = 'folge-user-account';
const PROPERTIES_STORAGE_KEY = 'folge-user-properties';

interface UserState {
  // Authentication state
  token: string | null;
  account: UserAccount | null;
  clientAppInfo: ClientAppInfo | null;
  properties: UserProperties | null;
  
  // Actions
  login: (token: string, account: UserAccount, properties: UserProperties, info: ClientAppInfo) => void;
  logout: () => void;
  setClientAppInfo: (info: ClientAppInfo) => void;
  updateProperties: (properties: Partial<UserProperties>) => void;
}

// Helper to get initial token from localStorage
const getInitialToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    console.error('Error reading token from localStorage:', e);
    return null;
  }
};

// Helper to get initial account from localStorage
const getInitialAccount = (): UserAccount | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(ACCOUNT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.error('Error reading account from localStorage:', e);
    return null;
  }
};

// Helper to get initial properties from localStorage
const getInitialProperties = (): UserProperties | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(PROPERTIES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.error('Error reading properties from localStorage:', e);
    return null;
  }
};

export const useUserStore = create<UserState>((set) => ({
  // Initial state - try to restore from localStorage on load
  token: getInitialToken(),
  account: getInitialAccount(),
  clientAppInfo: null,
  properties: getInitialProperties(),
  
  // Actions
  login: (token, account, properties, info) => {
    // Store token in localStorage
    try {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      localStorage.setItem(ACCOUNT_STORAGE_KEY, JSON.stringify(account));
      if (properties) {
        localStorage.setItem(PROPERTIES_STORAGE_KEY, JSON.stringify(properties));
      }
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
    
    // Also set cookie for server-side auth (server sets it too, but ensure it's there)
    setCookie('session-token', token, {
      maxAge: 30 * 24 * 60 * 60, // 30 days
      path: '/',
      sameSite: 'Lax'
    });
    
    set({
      token,
      account,
      properties,
      clientAppInfo: info
    });
  },
  
  logout: () => {
    // Clear localStorage
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(ACCOUNT_STORAGE_KEY);
      localStorage.removeItem(PROPERTIES_STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing localStorage:', e);
    }
    
    // Delete cookie
    deleteCookie('session-token');
    
    set({
      token: null,
      account: null,
      clientAppInfo: null,
      properties: null
    });
  },

  setClientAppInfo: (info) => set({ clientAppInfo: info }),
  
  updateProperties: (newProperties) => set((state) => {
    const updatedProperties = state.properties ? {
      ...state.properties,
      ...newProperties,
      portal: {
        ...state.properties.portal,
        ...newProperties.portal
      }
    } : null;
    
    // Persist to localStorage
    if (updatedProperties) {
      try {
        localStorage.setItem(PROPERTIES_STORAGE_KEY, JSON.stringify(updatedProperties));
      } catch (e) {
        console.error('Error saving properties to localStorage:', e);
      }
    }
    
    return { properties: updatedProperties };
  })
}));

const CloudV2Version = '1.28.0';

// Reactive selectors
export const useIsLoggedIn = () => useUserStore(state => !!state.token && !!state.account);
export const useUserHasCloudEnabled = () => useUserStore(state => !state.account?.cloudDisabled);
export const useUserHasHelpCenterEnabled = () => useUserStore(state => state.account?.helpCenterEnabled ?? false);
export const useIsProCloudPlan = () => useUserStore(state => state.properties?.portal?.billing?.isProPlan ?? false);
export const useBillingInfo = () => useUserStore(state => {
  return state.properties?.portal?.billing ?? null;
});
export const useIsAdmin = () => useUserStore(state => {
  const a = state.account as { isAdmin?: boolean; is_admin?: boolean } | null;
  return a?.isAdmin ?? a?.is_admin ?? false;
}); 
export const useClientAppSupportsCloudV2 = () => useUserStore(state => { 
  const appVersion = state.clientAppInfo?.appVersion;
  if (!appVersion) return false;
  return semverCompare(appVersion, CloudV2Version) >= 0;
});